// src/hooks/useAllCallsData.js
import { useState, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import useAuth from './useAuth';
import api from '../services/api';
import {
  useDateTimeFilter,
  toBackendFormat,
  toInputFormat,
  getDefaultDateFrom,
  getDefaultDateTo,
} from './useDateTimeFilter';
import { useFilterState } from './useFilterState';

export const useAllCallsData = () => {
  const { projectId } = useParams();
  const { user } = useAuth();
  const { state } = useLocation();
  const project = state?.project;

  const [calls, setCalls] = useState([]);
  const [themes, setThemes] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  // === Глобальное состояние для модалки ===
  const [activeTextSoundId, setActiveTextSoundId] = useState(null);

  // === Фильтры дат ===
  const dateTimeFilter = useDateTimeFilter('', '');
  const {
    filters: dateFilters,
    tempValues: tempDateValues,
    handleChange: handleDateChange,
    handleBlur: handleDateBlur,
  } = dateTimeFilter;

  // === Текстовые фильтры (рефакторинг) ===
  const textFilter = useFilterState({
    search_phone: '',
    search_operator: '',
    search_text: '',
    duration_value: '',
  });

  const {
    filters: textFilters,
    tempValues: tempTextValues,
    handleTextChange,
    handleTextBlur,
    handleTextKeyDown,
  } = textFilter;

  // === Прочие фильтры ===
  const [filters, setFilters] = useState({
    filter_stopside: '',
    duration_op: '',
    filter_obscene: '',
    filter_no_text: '',
    filter_unchecked: '',
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  // === Фильтры тем ===
  const [themeFilters, setThemeFilters] = useState({});
  const isInitialThemeFilters = useRef(false);

  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 100;
  const [isFetching, setIsFetching] = useState(false);

  // === Экспорт ===
  const [exportTaskId, setExportTaskId] = useState(null);
  const [exportProgress, setExportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  // === Загрузка данных ===
  const fetchData = async () => {
    if (!projectId) return;

    setIsFetching(true);
    try {
      const dateFromToSend = dateFilters.date_filter_from
        ? toBackendFormat(dateFilters.date_filter_from)
        : '';
      const dateToToSend = dateFilters.date_filter_to
        ? toBackendFormat(dateFilters.date_filter_to)
        : '';

      const params = new URLSearchParams({
        page: currentPage,
        project_id: projectId,
        date_filter_from: dateFromToSend,
        date_filter_to: dateToToSend,
        search_phone: textFilters.search_phone,
        search_operator: textFilters.search_operator,
        search_text: textFilters.search_text,
        filter_stopside: filters.filter_stopside,
        duration_op: filters.duration_op,
        duration_value: textFilters.duration_value || '',
        filter_obscene: filters.filter_obscene,
        filter_no_text: filters.filter_no_text,
        filter_unchecked: filters.filter_unchecked,
      });

      Object.keys(themeFilters).forEach((key) => {
        const id = Number(key);
        const value = themeFilters[key];
        if (value && ['yes', 'partial', 'no'].includes(value)) {
          params.append(`theme_${id}`, value);
        }
      });

      const res = await api.get(`/analytics/calls/${projectId}`, { params });
      const data = res.data;

      setCalls(data.calls || []);
      setThemes(data.themes || []);
      setTotalRecords(data.total || 0);

      let serverFrom = data.date_filter_from || getDefaultDateFrom();
      let serverTo = data.date_filter_to || getDefaultDateTo();

      const inputFrom = toInputFormat(serverFrom);
      const inputTo = toInputFormat(serverTo);

      if (dateFilters.date_filter_from !== inputFrom) {
        handleDateBlur({ target: { name: 'date_filter_from', value: inputFrom } });
      }
      if (dateFilters.date_filter_to !== inputTo) {
        handleDateBlur({ target: { name: 'date_filter_to', value: inputTo } });
      }

      if (!isInitialThemeFilters.current && data.themes?.length > 0) {
        const initialFilters = {};
        data.themes.forEach((t) => {
          initialFilters[t.id] = '';
        });
        setThemeFilters(initialFilters);
        isInitialThemeFilters.current = true;
      }
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
      setCalls([]);
      setTotalRecords(0);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId, currentPage, dateFilters, textFilters, filters, themeFilters]);

  // === Обработчики тем ===
  const handleThemeFilterChange = (themeId, value) => {
    const id = Number(themeId);
    setThemeFilters((prev) => ({ ...prev, [id]: value }));
    setCurrentPage(1);
  };

  // === Сброс фильтров ===
  const resetFilters = () => {
    dateTimeFilter.reset();
    textFilter.reset();
    setFilters({
      filter_stopside: '',
      duration_op: '',
      filter_obscene: '',
      filter_no_text: '',
      filter_unchecked: '',
    });
    setThemeFilters({});
    isInitialThemeFilters.current = false;
    setCurrentPage(1);
    setActiveTextSoundId(null);
  };

  // === Экспорт в Excel ===
  const exportToExcel = async () => {
    if (!user) {
      window.showToast('Доступ запрещён. Пожалуйста, авторизуйтесь.');
      return;
    }

    setIsExporting(true);
    setExportError('');
    setExportProgress(0);
    setExportTaskId(null);

    try {
      const dateFromToSend = dateFilters.date_filter_from
        ? toBackendFormat(dateFilters.date_filter_from)
        : '';
      const dateToToSend = dateFilters.date_filter_to
        ? toBackendFormat(dateFilters.date_filter_to)
        : '';

      const params = new URLSearchParams({
        project_id: projectId,
        date_filter_from: dateFromToSend,
        date_filter_to: dateToToSend,
        search_phone: textFilters.search_phone,
        search_operator: textFilters.search_operator,
        search_text: textFilters.search_text,
        filter_stopside: filters.filter_stopside,
        duration_op: filters.duration_op,
        duration_value: textFilters.duration_value || '',
        filter_obscene: filters.filter_obscene,
        filter_no_text: filters.filter_no_text,
        filter_unchecked: filters.filter_unchecked,
      });

      Object.keys(themeFilters).forEach((key) => {
        const id = Number(key);
        const value = themeFilters[key];
        if (value && ['yes', 'partial', 'no'].includes(value)) {
          params.append(`theme_${id}`, value);
        }
      });

      const res = await api.get(`/analytics/export/${projectId}`, { params });
      const { task_id } = res.data;

      setExportTaskId(task_id);
      setExportProgress(0);
      pollExportProgress(task_id);
    } catch (err) {
      console.error('Ошибка запуска экспорта:', err);
      setExportError('Не удалось запустить экспорт. Попробуйте позже.');
      setIsExporting(false);
    }
  };

  const pollExportProgress = async (task_id) => {
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/analytics/export/progress/${task_id}`);
        const data = res.data;

        setExportProgress(data.progress || 0);

        if (data.status === 'completed') {
          clearInterval(interval);
          setIsExporting(false);
          setExportTaskId(null);

          const link = document.createElement('a');
          link.href = `/analytics/export/download/${task_id}`;
          link.setAttribute('download', '');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else if (data.status === 'failed') {
          clearInterval(interval);
          setIsExporting(false);
          setExportError(data.error || 'Неизвестная ошибка экспорта');
          setExportTaskId(null);
        }
      } catch (err) {
        console.error('Ошибка опроса прогресса:', err);
        clearInterval(interval);
        setIsExporting(false);
        setExportError('Ошибка связи с сервером');
        setExportTaskId(null);
      }
    }, 1000);
  };

  // === Пагинация ===
  const totalPages = Math.ceil(totalRecords / perPage);
  const start = (currentPage - 1) * perPage + 1;
  const end = Math.min(currentPage * perPage, totalRecords);

  return {
    // Данные
    calls,
    themes,
    totalRecords,
    project,
    user,
    // Фильтры
    filters: { ...textFilters, ...filters },
    themeFilters,
    dateFilters,
    tempTextValues,
    tempDateValues,
    // Обработчики
    handleTextChange,
    handleTextBlur,
    handleTextKeyDown,
    handleFilterChange,
    handleThemeFilterChange,
    resetFilters,
    handleDateChange,
    handleDateBlur,
    exportToExcel,
    // Пагинация
    currentPage,
    setCurrentPage,
    totalPages,
    start,
    end,
    // Состояние
    isFetching,
    perPage,
    // Экспорт
    isExporting,
    exportProgress,
    exportError,
    // Модалка текста
    activeTextSoundId,
    setActiveTextSoundId,
  };
};

export default useAllCallsData;
